// src/components/issues/DeclarationNcs.tsx — SCE Issues
// « Déclarer une NC-S » depuis la fiche problème (05/10, ouvert à tous les
// internes le 06/10) : le formulaire de réclamation fournisseur de la qualité,
// pré-rempli, déposé comme un fichier dans le dossier des déclarations NC-S
// sur SharePoint. Remplace le fichier Excel rempli à la main et envoyé par
// mail. La fiche ajoute ensuite le badge NC-S et une ligne d'activité
// (`onDeclaree`).
//
// ─── La fenêtre (06/10) ───
//
// Elle ne part plus par accident : ni le fond ni une sélection de texte
// glissée ne la ferment. « Réduire » (ou Échap) la range en gardant la
// saisie — un BROUILLON par problème, écrit à chaque frappe dans le
// navigateur (utils/brouillonNcs) — et la fiche propose « Reprendre ».
// « Abandonner » efface, après confirmation.
// Au bureau large, depuis une fiche en surimpression : LA fenêtre s'élargit et
// se divise — la NC-S en volet principal à droite (filet jaune), la fiche à
// gauche pour référence (fond gris), où l'on copie ce qu'il faut. Une seule
// fenêtre : on sait laquelle est la principale. Sinon : centrée (bureau), par
// le bas (téléphone).
//
// ─── Le parcours (retours de Guillaume des 05 et 06/10) ───
//
// Le formulaire s'ouvre TOUT DE SUITE, curseur dans le NVS (déjà « NVS ») :
// on commence par ce que l'appli ne sait pas — le composant fautif (NVS,
// désignation, N° de série DE LA PIÈCE, quantité) et la catégorie de défaut. Puis la
// priorité (proposée d'après la criticité) et la description (reprise du
// problème). Ce qui est déjà juste — lieu, initiateur, dates — tient en une
// ligne de résumé, « Modifier » pour l'ouvrir. Ctrl + Entrée envoie.
//
// Lu en arrière-plan : le formulaire vierge (sa liste des personnes —
// l'initiateur s'écrit comme dans la liste déroulante). Les déclarations déjà
// faites pour ce problème viennent de son activité (`dejaDeclarees`).

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type JSX, type KeyboardEvent, type ReactNode } from 'react'
import { CheckCircle2, ExternalLink, FileSpreadsheet } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Confirmation } from '@/components/ui/Confirmation'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useEstBureau, useMediaQuery } from '@/hooks/useMediaQuery'
import { useDansModale, useVoletModale } from '@/components/layout/ModaleRoute'
import { ajouterComposantSiNouveau, listeComposants } from '@/api/composants.api'
import { composantDuNvs, nvsAjoutable, rechercherComposants, type Composant } from '@/utils/composants'
import { effacerBrouillonNcs, ecrireBrouillonNcs, lireBrouillonNcs, noterFenetreNcs, saisieModifiee } from '@/utils/brouillonNcs'
import { useConfirmerEcriture } from '@/features/vue-role/contexte'
import {
  declarerNcs,
  lireModeleNcs,
  messageErreurNcs,
  type ModeleNcs,
  type ResultatDeclarationNcs,
} from '@/api/declarationNcs.api'
import {
  CATEGORIES_PROBLEME,
  LIBELLES_CATEGORIES,
  LIBELLES_LIEUX,
  LIEUX_DETECTION,
  PRIORITES,
  champsManquants,
  initiateurDepuis,
  nvsSaisi,
  preremplirNcs,
  type DeclarationNcs as Declaration,
} from '@/utils/declarationNcs'
import { A_QUALIFIER, type Equipement, type Issue } from '@/types/issue.types'
import { ECRAN_VOLET, Fenetre, STYLE_AIDE, STYLE_CHAMP, STYLE_ETIQUETTE, STYLE_SECTION, type Disposition } from './FenetreQualite'





/** `Date` locale ↔ valeur d'un `<input type="date">`. */
function versChamp(d: Date): string {
  const p = (n: number): string => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

function depuisChamp(v: string, repli: Date): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v)
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : repli
}

function jourCourt(d: Date): string {
  const p = (n: number): string => String(n).padStart(2, '0')
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`
}

type Lecture =
  | { etat: 'lecture' }
  | { etat: 'erreur'; message: string }
  | { etat: 'pret'; modele: ModeleNcs }

export interface DeclarationNcsProps {
  issue: Issue
  equipement: Equipement | null | undefined
  /** Fichiers des déclarations déjà déposées pour ce problème (activité). */
  dejaDeclarees: readonly string[]
  /** Fichier déposé : la fiche pose le badge NC-S et trace l'événement. */
  onDeclaree: (resultat: ResultatDeclarationNcs, priorite: Declaration['priorite']) => Promise<void>
  /** La fenêtre se range : réduite (brouillon gardé), abandonnée ou terminée. */
  onClose: () => void
}

export function DeclarationNcs({ issue, equipement, dejaDeclarees, onDeclaree, onClose }: DeclarationNcsProps): JSX.Element {
  const { nomReel, user } = useCurrentUser()
  const estBureau = useEstBureau()
  const large = useMediaQuery(ECRAN_VOLET)
  const dansModale = useDansModale()
  const { volet, ouvrirVolet } = useVoletModale()
  const confirmerEcriture = useConfirmerEcriture()
  const nom = nomReel || user?.nom || ''
  const disposition: Disposition = dansModale && large && volet ? 'volet' : estBureau ? 'centre' : 'bas'

  // Fenêtre ouverte : la pastille « NC-S en cours » (globale) s'efface.
  useEffect(() => {
    noterFenetreNcs(issue.id, true)
    return () => noterFenetreNcs(issue.id, false)
  }, [issue.id])

  // En volet : la fenêtre de la fiche s'élargit et se divise le temps de la NC-S.
  useEffect(() => {
    if (disposition !== 'volet') return
    ouvrirVolet(true)
    return () => ouvrirVolet(false)
  }, [disposition, ouvrirVolet])

  // Pré-remplie à l'ouverture, sans attendre le classeur : l'initiateur est
  // remis au format de la liste dès qu'elle arrive (s'il n'a pas été touché).
  // Un brouillon de ce problème, s'il y en a un, reprend sa place.
  const initiateurProvisoire = useRef(initiateurDepuis(nom, []))
  const depart = useRef<Declaration>(
    preremplirNcs(issue, equipement, initiateurProvisoire.current, `${window.location.origin}/issues/${issue.id}`, new Date()),
  )
  const [brouillonInitial] = useState(() => lireBrouillonNcs(issue.id))
  const [reprisLe, setReprisLe] = useState<string | null>(brouillonInitial?.majLe ?? null)
  const [saisie, setSaisie] = useState<Declaration>(() => brouillonInitial?.saisie ?? depart.current)
  const [lecture, setLecture] = useState<Lecture>({ etat: 'lecture' })
  const [essai, setEssai] = useState(0)
  const [detectionOuverte, setDetectionOuverte] = useState(false)
  const [envoi, setEnvoi] = useState(false)
  const [erreurEnvoi, setErreurEnvoi] = useState<string | null>(null)
  const [resultat, setResultat] = useState<ResultatDeclarationNcs | null>(null)

  useEffect(() => {
    let annule = false
    setLecture({ etat: 'lecture' })
    lireModeleNcs()
      .then((modele) => {
        if (annule) return
        setLecture({ etat: 'pret', modele })
        const initiateur = initiateurDepuis(nom, modele.personnes)
        setSaisie((s) => (s.initiateur === initiateurProvisoire.current ? { ...s, initiateur } : s))
        depart.current = { ...depart.current, initiateur }
        initiateurProvisoire.current = initiateur
      })
      .catch((err: unknown) => {
        console.error('[DeclarationNcs] lecture du modèle :', err)
        if (!annule) setLecture({ etat: 'erreur', message: messageErreurNcs(err) })
      })
    return () => {
      annule = true
    }
    // Lu à l'ouverture (et sur « Réessayer ») : pas à chaque rendu de la fiche.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [essai])

  const manquants = useMemo(() => champsManquants(saisie), [saisie])
  // Le résumé s'ouvre de lui-même s'il lui manque quelque chose.
  const detectionIncomplete = !saisie.lieuDetection || !saisie.initiateur.trim()

  const modifier = <K extends keyof Declaration>(cle: K, valeur: Declaration[K]): void =>
    setSaisie((s) => ({ ...s, [cle]: valeur }))

  // ─── Liste des composants NVS (06/10) : lue en arrière-plan, silencieuse si absente ───
  const [composants, setComposants] = useState<Composant[] | null>(null)
  useEffect(() => {
    let annule = false
    listeComposants()
      .then((liste) => {
        if (!annule) setComposants(liste)
      })
      .catch((err: unknown) => console.warn('[DeclarationNcs] liste NVS illisible :', err))
    return () => {
      annule = true
    }
  }, [])
  const choisirComposant = (c: Composant): void =>
    setSaisie((s) => ({ ...s, nvs: c[0], designationComposant: c[1] || s.designationComposant }))
  const connu = composants ? composantDuNvs(composants, saisie.nvs) : undefined
  const repereNvs = !composants || !nvsSaisi(saisie.nvs)
    ? null
    : connu
      ? `Dans la liste : ${[connu[1], connu[2]].filter(Boolean).join(' · ')}`
      : nvsAjoutable(saisie.nvs)
        ? 'Nouveau composant : il sera ajouté à la liste NVS à l’envoi.'
        : null

  // ─── Brouillon : écrit à chaque saisie (un peu après la dernière frappe) ───
  const derniereSaisie = useRef(saisie)
  derniereSaisie.current = saisie
  const termine = useRef(false)
  const garder = useCallback((): void => {
    if (termine.current) return
    if (saisieModifiee(derniereSaisie.current, depart.current)) ecrireBrouillonNcs(issue.id, derniereSaisie.current)
    else effacerBrouillonNcs(issue.id)
  }, [issue.id])
  useEffect(() => {
    const minuterie = window.setTimeout(garder, 300)
    return () => window.clearTimeout(minuterie)
  }, [saisie, garder])
  // Fiche refermée, page quittée : la dernière frappe est gardée aussi.
  useEffect(() => () => garder(), [garder])

  const reduire = useCallback((): void => {
    garder()
    onClose()
  }, [garder, onClose])

  // « Abandonner » : confirmation aux couleurs de l'appli s'il y a une saisie à perdre.
  const [confirmerAbandon, setConfirmerAbandon] = useState(false)
  const abandonnerPourDeBon = (): void => {
    termine.current = true
    effacerBrouillonNcs(issue.id)
    onClose()
  }
  const abandonner = (): void => {
    if (saisieModifiee(saisie, depart.current)) setConfirmerAbandon(true)
    else abandonnerPourDeBon()
  }

  const repartirDeZero = (): void => {
    setSaisie(depart.current)
    setReprisLe(null)
    effacerBrouillonNcs(issue.id)
  }

  // Échap réduit la NC-S — et seulement elle : la fiche en dessous reste ouverte.
  useEffect(() => {
    function surEchap(e: globalThis.KeyboardEvent): void {
      if (e.key !== 'Escape') return
      // Suggestions ouvertes, ou confirmation d'abandon : c'est elles qu'Échap referme.
      if (confirmerAbandon || document.activeElement?.getAttribute('aria-expanded') === 'true') return
      e.stopImmediatePropagation()
      if (!envoi) reduire()
    }
    window.addEventListener('keydown', surEchap, true)
    return () => window.removeEventListener('keydown', surEchap, true)
  }, [envoi, reduire, confirmerAbandon])

  async function envoyer(): Promise<void> {
    if (envoi || manquants.length > 0) return
    // Bascule de rôle : le fichier et le badge partent sous le compte réel.
    if (!confirmerEcriture('Envoyer la NC-S à la qualité')) return
    setEnvoi(true)
    setErreurEnvoi(null)
    try {
      const r = await declarerNcs(issue.id, saisie, lecture.etat === 'pret' ? lecture.modele : undefined)
      // Le fichier est déposé : c'est acquis. Badge et activité suivent, la
      // fiche dit elle-même s'ils échouent.
      await onDeclaree(r, saisie.priorite)
      termine.current = true
      effacerBrouillonNcs(issue.id)
      setResultat(r)
      // Composant nouveau : ajouté à la liste NVS, au mieux (sans gêner la NC-S).
      void ajouterComposantSiNouveau(nvsSaisi(saisie.nvs), saisie.designationComposant)
    } catch (err) {
      console.error('[DeclarationNcs] écriture :', err)
      setErreurEnvoi(messageErreurNcs(err))
    } finally {
      setEnvoi(false)
    }
  }


  // Ctrl + Entrée (⌘ + Entrée) envoie, depuis n'importe quel champ.
  const surTouche = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      void envoyer()
    }
  }

  // ─── Terminé ───
  if (resultat) {
    return (
      <Fenetre
        disposition={disposition}
        volet={volet}
        titre="Déclaration NC-S"
        onReduire={onClose}
        libelleReduire="Fermer"
        pied={
          <>
            <Button variant="ghost" onClick={onClose}>Fermer</Button>
            <a
              href={resultat.lien}
              target="_blank"
              rel="noreferrer"
              className="flex-1 flex items-center justify-center gap-2 rounded-lg"
              style={{ height: 40, backgroundColor: 'var(--color-yellow)', color: 'var(--color-black)', fontWeight: 500, textDecoration: 'none' }}
            >
              <ExternalLink size={16} />
              Ouvrir dans Excel
            </a>
          </>
        }
      >
        <div className="flex items-start gap-3">
          <CheckCircle2 size={24} color="var(--color-traffic-green)" style={{ flexShrink: 0 }} />
          <div className="flex flex-col gap-1">
            <p style={{ margin: 0, fontWeight: 500 }}>NC-S envoyée à la qualité</p>
            <p style={{ ...STYLE_AIDE, overflowWrap: 'anywhere' }}>
              {resultat.fichier} — rangée dans le dossier « Déclarations NC-S », la qualité la reçoit par mail. Le
              problème porte maintenant le badge NC-S ; sa référence s&apos;ajoutera quand elle sera connue.
            </p>
          </div>
        </div>
      </Fenetre>
    )
  }

  const priorite = PRIORITES.find((p) => p.valeur === saisie.priorite)

  // ─── Saisie ───
  return (
    <Fenetre
      disposition={disposition}
      volet={volet}
      titre="Déclarer une NC-S"
      sousTitre={`${issue.id} · ${issue.titre}`}
      onReduire={reduire}
      libelleReduire="Réduire"
      desactive={envoi}
      pied={
        <div className="flex flex-col gap-2 w-full">
          <p style={{ ...STYLE_AIDE, color: erreurEnvoi ? 'var(--color-traffic-red)' : STYLE_AIDE.color }} role="status">
            {erreurEnvoi ??
              (manquants.length > 0
                ? `Il manque ${manquants.join(', ')}.`
                : `Prêt à déposer.${estBureau ? ' Ctrl + Entrée pour envoyer.' : ''}`)}
          </p>
          <div className="flex gap-3">
            <Button variant="ghost" onClick={abandonner} disabled={envoi}>Abandonner</Button>
            <Button
              variant="primary"
              fullWidth
              onClick={() => void envoyer()}
              disabled={envoi || manquants.length > 0}
              iconLeft={<FileSpreadsheet size={16} />}
            >
              {envoi ? 'Dépôt…' : 'Déclarer la NC-S'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-5" onKeyDown={surTouche}>
        {confirmerAbandon && (
          <Confirmation
            titre="Abandonner cette NC-S ?"
            confirmer="Abandonner"
            annuler="Continuer la saisie"
            danger
            onConfirmer={abandonnerPourDeBon}
            onAnnuler={() => setConfirmerAbandon(false)}
          >
            Ce qui a été saisi sera effacé. Pour la reprendre plus tard, utilisez plutôt « Réduire ».
          </Confirmation>
        )}
        {reprisLe && (
          <div className="flex items-center justify-between gap-3" style={STYLE_ENCADRE}>
            <span>Votre saisie du {dateHeure(reprisLe)} est reprise.</span>
            <Button variant="ghost" size="sm" onClick={repartirDeZero} disabled={envoi}>Repartir de zéro</Button>
          </div>
        )}
        {lecture.etat === 'erreur' && (
          <div className="flex items-center justify-between gap-3" style={{ ...STYLE_ENCADRE, color: 'var(--color-traffic-red)' }}>
            <span>{lecture.message}</span>
            <Button variant="ghost" size="sm" onClick={() => setEssai((n) => n + 1)}>Réessayer</Button>
          </div>
        )}
        {dejaDeclarees.length > 0 && (
          <p style={STYLE_ENCADRE}>
            Déjà {dejaDeclarees.length > 1 ? `${dejaDeclarees.length} déclarations` : 'une déclaration'} pour ce problème (
            {dejaDeclarees.join(', ')}). Celle-ci s&apos;ajoute : un autre composant, par exemple.
          </p>
        )}

        {/* 1. Ce que l'appli ne sait pas : le composant. */}
        <section className="flex flex-col gap-4">
          <h4 style={STYLE_SECTION}>Composant défectueux</h4>
          {/* NVS et désignation cherchent dans la liste des composants (06/10) :
              un choix remplit les deux. */}
          <Champ id="ncs-nvs" libelle="N° NVS">
            <ChampComposant
              id="ncs-nvs"
              valeur={saisie.nvs}
              onChange={(v) => modifier('nvs', v)}
              onChoisir={choisirComposant}
              composants={composants}
              autoFocus={estBureau}
              curseurALaFin
              desactive={envoi}
              style={{ ...STYLE_CHAMP, fontVariantNumeric: 'tabular-nums', letterSpacing: 0.3 }}
            />
            {repereNvs && <p style={{ ...STYLE_AIDE, marginTop: 4 }}>{repereNvs}</p>}
          </Champ>
          <Champ id="ncs-designation" libelle="Désignation *">
            <ChampComposant
              id="ncs-designation"
              valeur={saisie.designationComposant}
              onChange={(v) => modifier('designationComposant', v)}
              onChoisir={choisirComposant}
              composants={composants}
              placeholder="Telle que sur l'étiquette — ou cherchez dans la liste"
              desactive={envoi}
              style={STYLE_CHAMP}
            />
          </Champ>
          <div className="grid gap-3" style={{ gridTemplateColumns: 'minmax(0, 1fr) 88px' }}>
            <Champ id="ncs-serie" libelle="N° de série pièce">
              <input
                id="ncs-serie"
                type="text"
                value={saisie.numeroSerie}
                onChange={(e) => modifier('numeroSerie', e.target.value)}
                disabled={envoi}
                className="h-12 px-3 rounded-lg outline-none"
                style={STYLE_CHAMP}
              />
            </Champ>
            <Champ id="ncs-quantite" libelle="Quantité *">
              <input
                id="ncs-quantite"
                type="number"
                inputMode="numeric"
                min={1}
                value={saisie.quantite ?? ''}
                onChange={(e) => modifier('quantite', e.target.value === '' ? null : Number(e.target.value))}
                disabled={envoi}
                className="h-12 px-3 rounded-lg outline-none"
                style={STYLE_CHAMP}
              />
            </Champ>
          </div>
          <div>
            <Champ id="ncs-categorie" libelle="Catégorie de problème *">
              <select
                id="ncs-categorie"
                value={saisie.categorie}
                onChange={(e) => modifier('categorie', e.target.value)}
                disabled={envoi}
                className="h-12 px-3 rounded-lg outline-none"
                style={{ ...STYLE_CHAMP, color: saisie.categorie ? 'var(--color-black)' : 'var(--color-text-secondary)' }}
              >
                <option value="">Choisir…</option>
                {CATEGORIES_PROBLEME.map((c) => (
                  <option key={c} value={c}>{LIBELLES_CATEGORIES[c] ?? c}</option>
                ))}
              </select>
            </Champ>
          </div>
        </section>

        {/* 2. Priorité : proposée d'après la criticité, un clic pour changer. */}
        <section className="flex flex-col gap-2">
          <span id="ncs-priorite" style={STYLE_SECTION}>Priorité *</span>
          <div
            role="radiogroup"
            aria-labelledby="ncs-priorite"
            className="grid"
            style={{ gridTemplateColumns: 'repeat(3, 1fr)', border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden' }}
          >
            {PRIORITES.map((p, k) => {
              const choisie = saisie.priorite === p.valeur
              return (
                <button
                  key={p.valeur}
                  type="button"
                  role="radio"
                  aria-checked={choisie}
                  title={p.libelle}
                  onClick={() => modifier('priorite', p.valeur)}
                  disabled={envoi}
                  style={{
                    minHeight: 40,
                    padding: '0 8px',
                    border: 'none',
                    borderLeft: k > 0 ? '1px solid var(--color-border)' : 'none',
                    backgroundColor: choisie ? 'var(--color-black)' : 'var(--color-white)',
                    color: choisie ? 'var(--color-white)' : 'var(--color-black)',
                    fontSize: 14,
                    fontWeight: choisie ? 600 : 400,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {p.valeur} · {p.court}
                </button>
              )
            })}
          </div>
          <p style={STYLE_AIDE}>
            {priorite ? priorite.libelle : 'Production bloquée, dégradée, ou sans impact ?'}
            {issue.criticite !== A_QUALIFIER && ` — proposée d'après la criticité « ${issue.criticite} ».`}
          </p>
          <input
            id="ncs-explication"
            type="text"
            aria-label="Explication de la priorité"
            placeholder="Pourquoi cette priorité ? — facultatif"
            value={saisie.explicationPriorite}
            onChange={(e) => modifier('explicationPriorite', e.target.value)}
            disabled={envoi}
            className="h-11 px-3 rounded-lg outline-none"
            style={STYLE_CHAMP}
          />
        </section>

        {/* 3. Description : reprise du problème, à compléter. */}
        <section className="flex flex-col gap-4">
          <h4 style={STYLE_SECTION}>Description</h4>
          <Champ id="ncs-description" libelle="Non-conformité constatée *" aide="Reprise du problème ; le lien renvoie à la fiche (photos, historique).">
            <textarea
              id="ncs-description"
              value={saisie.description}
              onChange={(e) => modifier('description', e.target.value)}
              disabled={envoi}
              rows={6}
              className="p-3 rounded-lg outline-none resize-y"
              style={STYLE_CHAMP}
            />
          </Champ>
          <Champ id="ncs-specification" libelle="Spécification / exigence — facultatif">
            <textarea
              id="ncs-specification"
              placeholder="Plan, schéma, norme : ce que la pièce aurait dû respecter"
              value={saisie.specification}
              onChange={(e) => modifier('specification', e.target.value)}
              disabled={envoi}
              rows={2}
              className="p-3 rounded-lg outline-none resize-y"
              style={STYLE_CHAMP}
            />
          </Champ>
        </section>

        {/* 4. Détection : déjà rempli, résumé en une ligne. */}
        <section className="flex flex-col gap-4" style={{ borderTop: '1px solid var(--color-gray-10)', paddingTop: 12 }}>
          {detectionOuverte || detectionIncomplete ? (
            <>
              <h4 style={STYLE_SECTION}>Détection</h4>
              <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
                <Champ id="ncs-lieu" libelle="Lieu de détection *">
                  <select
                    id="ncs-lieu"
                    value={saisie.lieuDetection}
                    onChange={(e) => modifier('lieuDetection', e.target.value)}
                    disabled={envoi}
                    className="h-12 px-3 rounded-lg outline-none"
                    style={STYLE_CHAMP}
                  >
                    <option value="">Choisir…</option>
                    {LIEUX_DETECTION.map((l) => (
                      <option key={l} value={l}>{LIBELLES_LIEUX[l] ?? l}</option>
                    ))}
                  </select>
                </Champ>
                <Champ id="ncs-initiateur" libelle="Initiateur *">
                  <input
                    id="ncs-initiateur"
                    type="text"
                    list="ncs-personnes"
                    value={saisie.initiateur}
                    onChange={(e) => modifier('initiateur', e.target.value)}
                    disabled={envoi}
                    className="h-12 px-3 rounded-lg outline-none"
                    style={STYLE_CHAMP}
                  />
                  {lecture.etat === 'pret' && (
                    <datalist id="ncs-personnes">
                      {lecture.modele.personnes.map((p) => <option key={p} value={p} />)}
                    </datalist>
                  )}
                </Champ>
                <Champ id="ncs-occurrence" libelle="Survenu le">
                  <input
                    id="ncs-occurrence"
                    type="date"
                    value={versChamp(saisie.dateOccurrence)}
                    onChange={(e) => modifier('dateOccurrence', depuisChamp(e.target.value, saisie.dateOccurrence))}
                    disabled={envoi}
                    className="h-12 px-3 rounded-lg outline-none"
                    style={STYLE_CHAMP}
                  />
                </Champ>
                <Champ id="ncs-notification" libelle="Notifié à la qualité le">
                  <input
                    id="ncs-notification"
                    type="date"
                    value={versChamp(saisie.dateNotification)}
                    onChange={(e) => modifier('dateNotification', depuisChamp(e.target.value, saisie.dateNotification))}
                    disabled={envoi}
                    className="h-12 px-3 rounded-lg outline-none"
                    style={STYLE_CHAMP}
                  />
                </Champ>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-between gap-3">
              <p style={{ margin: 0, fontSize: 14, lineHeight: '20px' }}>
                <span style={{ color: 'var(--color-text-secondary)' }}>Détecté en </span>
                {(LIBELLES_LIEUX[saisie.lieuDetection] ?? saisie.lieuDetection).toLowerCase()}
                <span style={{ color: 'var(--color-text-secondary)' }}> le </span>
                {jourCourt(saisie.dateOccurrence)}
                <span style={{ color: 'var(--color-text-secondary)' }}> · déclaré par </span>
                {saisie.initiateur.trim()}
                <span style={{ color: 'var(--color-text-secondary)' }}> le </span>
                {jourCourt(saisie.dateNotification)}
              </p>
              <Button variant="ghost" size="sm" onClick={() => setDetectionOuverte(true)} disabled={envoi}>
                Modifier
              </Button>
            </div>
          )}
        </section>
      </div>
    </Fenetre>
  )
}

const STYLE_ENCADRE: CSSProperties = {
  margin: 0,
  padding: '10px 12px',
  borderRadius: 8,
  backgroundColor: 'var(--color-gray-5)',
  fontSize: 'var(--font-size-copy2)',
}

function Champ({ id, libelle, aide, children }: { id: string; libelle: string; aide?: string; children: ReactNode }): JSX.Element {
  return (
    <div style={{ minWidth: 0 }}>
      <label htmlFor={id} style={STYLE_ETIQUETTE}>{libelle}</label>
      {children}
      {aide && <p style={{ ...STYLE_AIDE, marginTop: 4 }}>{aide}</p>}
    </div>
  )
}

/** « 06/10 à 09:42 » */
function dateHeure(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const p = (n: number): string => String(n).padStart(2, '0')
  return `${p(d.getDate())}/${p(d.getMonth() + 1)} à ${p(d.getHours())}:${p(d.getMinutes())}`
}


/**
 * Un champ qui cherche dans la liste des composants (NVS ou désignation) :
 * les suggestions s'ouvrent sous le champ, ↑ ↓ pour choisir, Entrée pour
 * prendre, Échap pour refermer. Un choix remplit NVS ET désignation.
 */
function ChampComposant({
  id,
  valeur,
  onChange,
  onChoisir,
  composants,
  placeholder,
  autoFocus,
  curseurALaFin,
  desactive,
  style,
}: {
  id: string
  valeur: string
  onChange: (v: string) => void
  onChoisir: (c: Composant) => void
  composants: Composant[] | null
  placeholder?: string
  autoFocus?: boolean
  /** Curseur après le texte déjà là (« NVS ») à la prise du focus. */
  curseurALaFin?: boolean
  desactive?: boolean
  style: CSSProperties
}): JSX.Element {
  const [ouvert, setOuvert] = useState(false)
  const [actif, setActif] = useState(0)
  const suggestions = useMemo(
    () => (composants && ouvert ? rechercherComposants(composants, valeur) : []),
    [composants, ouvert, valeur],
  )
  // Rien à proposer si la saisie EST déjà le composant choisi.
  const visibles = suggestions.length === 1 && (suggestions[0]?.[0] === valeur.trim().toUpperCase() || suggestions[0]?.[1] === valeur.trim())
    ? []
    : suggestions
  const deplie = ouvert && visibles.length > 0

  const prendre = (c: Composant): void => {
    onChoisir(c)
    setOuvert(false)
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        id={id}
        type="text"
        role="combobox"
        aria-expanded={deplie}
        aria-controls={`${id}-suggestions`}
        aria-autocomplete="list"
        autoComplete="off"
        spellCheck={false}
        autoFocus={autoFocus}
        placeholder={placeholder}
        value={valeur}
        disabled={desactive}
        className="h-12 px-3 rounded-lg outline-none"
        style={style}
        onFocus={(e) => {
          setOuvert(true)
          if (curseurALaFin) {
            const champ = e.currentTarget
            window.setTimeout(() => champ.setSelectionRange(champ.value.length, champ.value.length))
          }
        }}
        onBlur={() => window.setTimeout(() => setOuvert(false), 150)}
        onChange={(e) => {
          onChange(e.target.value)
          setOuvert(true)
          setActif(0)
        }}
        onKeyDown={(e) => {
          if (!deplie) return
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setActif((a) => (a + 1) % visibles.length)
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActif((a) => (a - 1 + visibles.length) % visibles.length)
          } else if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) {
            e.preventDefault()
            const c = visibles[actif]
            if (c) prendre(c)
          } else if (e.key === 'Escape') {
            // Ne referme que la liste : ni la NC-S, ni la fiche dessous.
            e.nativeEvent.stopImmediatePropagation()
            setOuvert(false)
          }
        }}
      />
      {deplie && (
        <ul id={`${id}-suggestions`} role="listbox" className="ncs-suggestions">
          {visibles.map((c, k) => (
            <li
              key={c[0]}
              role="option"
              aria-selected={k === actif}
              className="ncs-suggestion"
              data-actif={k === actif}
              onMouseDown={(e) => {
                e.preventDefault()
                prendre(c)
              }}
              onMouseEnter={() => setActif(k)}
            >
              <span className="ncs-suggestion-nvs">{c[0]}</span>
              <span className="ncs-suggestion-designation">{c[1]}</span>
              {c[2] && <span className="ncs-suggestion-categorie">{c[2]}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
